import {
    addDays,
    addHours,
    format,
    formatISO,
    parse,
    parseISO,
    subDays,
    subMonths,
} from 'date-fns';
import { TimezoneOffset } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';

export interface IDateRange {
    startDate: Date; // inclusive
    endDate: Date; // inclusive
}

const isDate = (date: unknown) => date && (date instanceof Date);

/**
 * @param   {String} dateString the string to parse
 * @param   {String} dateFormat the format of date string
 * @param   {Number} fromTimeZone the timeZone of date String
 * @returns {Date} the parsed date
 */
export function parseDate(dateString: string, dateFormat: string, fromTimeZone: number | null = null): Date {
    const date = parse(dateString, dateFormat, new Date());
    if (fromTimeZone !== null) {
        const currentTimeZone = -Math.round(date.getTimezoneOffset() / 60);
        return new Date(date.getTime() + (currentTimeZone - fromTimeZone) * 60 * 60 * 1000);
    }
    return date;
}

/**
 * @param   {String} ISOString ISO format string. Ex: '2014-02-11T11:30:30'
 * @returns {Date} the parsed date
 */
export function parseISOString(ISOString: string): Date {
    return parseISO(ISOString);
}

/**
 * @param   {Date} date the Date object
 * @returns ISO format string. Ex: '2014-02-11T11:30:30'
 */
export function formatISOString(date: Date): string {
    return formatISO(date);
}

/**
 * @param   {Date} date the Date object
 * @param   {String} toFormat the target format of date
 * @returns {String} the formatted date string
 */
export function formatDate(date: Date, toFormat: string): string {
    if (!isDate(date)) {
        throw new TypeError('Invalid date type, must be Date object');
    }
    return format(date, toFormat);
}

/**
 * @param   {String} dateString the Date object
 * @param   {String} fromFormat the format of date string
 * @param   {String} toFormat the target format of date
 * @returns {String} the formatted date string
 */
export function formatDateString(dateString: string, fromFormat: string, toFormat: string): string {
    const date = parseDate(dateString, fromFormat);
    return formatDate(date, toFormat);
}

export function formatToServerTime(date: Date, toFormat: string): string {
    return formatDate(getTimeWithGMT(date, TimezoneOffset.Server), toFormat);
}

export function formatToSingaporeTime(date: Date, toFormat: string): string {
    return formatDate(getTimeWithGMT(date, TimezoneOffset.Singapore), toFormat);
}

export function formatDateWithYearToServerTime(date: Date): string {
    return formatToServerTime(date, i18n.t('dateFormat_date_month_year'));
}

export function formatDateWithYearToSingaporeTime(date: Date): string {
    return formatToSingaporeTime(date, i18n.t('dateFormat_date_month_year'));
}

export function getWeekday(day: number, gmtOffset: number) {
    const today = getNowWithGMT(gmtOffset);
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return weekdays[addDay(today, day).getDay()];
}

export function getDateAfterTodayWithMonth(day: number, gmtOffset: number) {
    const today = getNowWithGMT(gmtOffset);
    return formatDate(addDay(today, day), i18n.t('dateFormat_date_month').toString());
}

export function getDateWithMonth(date: Date) {
    return formatDate(date, i18n.t('dateFormat_date_month').toString());
}

export function getDateWithYear(date: Date) {
    return formatDate(date, i18n.t('dateFormat_date_month_year').toString());
}

export function getMonthWithYear(date: Date) {
    return formatDate(date, i18n.t('dateFormat_month_year').toString());
}

export function addDay(date: Date, days: number) {
    return addDays(date, days);
}

export function addHour(date: Date, hours: number) {
    return addHours(date, hours);
}

export function subDay(date: Date, days: number) {
    return subDays(date, days);
}

export function subMonth(date: Date, months: number) {
    return subMonths(date, months);
}

/**
 * @param   {Number} gmtOffset GMT timeZone hour offset(Ex: GMT+8 will be +8, GMT-2 will be -2)
 * @returns {Date} the dateTime with specific offset
 */
export function getTimeWithGMT(date: Date, gmtOffset: number): Date {
    if (!isDate(date)) {
        throw new TypeError('Invalid date type, must be Date object');
    }
    // getTimezoneOffset will return minutes
    const timezoneOffset = date.getTimezoneOffset() / 60;

    return new Date(date.getTime() + (timezoneOffset + gmtOffset) * 60 * 60 * 1000);
}

/**
 * @param   {Number} gmtOffset GMT timeZone hour offset(Ex: GMT+8 will be +8, GMT-2 will be -2)
 * @returns {Date} the dateTime with specific offset
 */
export function getNowWithGMT(gmtOffset: number): Date {
    const now = new Date();
    return getTimeWithGMT(now, gmtOffset);
}

/**
 * @param   {Number} gmtOffset GMT timeZone hour offset(Ex: GMT+8 will be +8, GMT-2 will be -2)
 * @returns {Date} the dateTime with specific offset without the time component
 */
export function getTodayWithGMT(gmtOffset: number): Date {
    const result = getNowWithGMT(gmtOffset);
    result.setHours(0, 0, 0, 0);
    return result;
}

export function formatDateToServerTimeString(date: Date) {
    return `${formatDate(date, 'yyyy-MM-dd')}T00:00:00-0400`;
}

export function convertDateFromSourceToUserTimeZone(date: Date, sourceUtcOffsetHours: number) {
    const userUtcOffsetMinutes = -date.getTimezoneOffset(); // +8 -> 480
    const sourceUtcOffsetMinutes = sourceUtcOffsetHours * 60; // 1 -> 60

    const shiftMinutesFromSourceToUser = userUtcOffsetMinutes - sourceUtcOffsetMinutes;

    return new Date(
        date.getTime() + shiftMinutesFromSourceToUser * 60_000,
    );
}
